/* @flow */

import { intlShape } from 'react-intl';
import {
  getEventProfilePositionLabel,
  getEventProfileCompanyLabel,
} from '@e180/domain/lib/event/util';
import { assertObject } from '@e180/util/lib/assert';

import messages from 'src/shared/app/market/l10n/marketL10n';
import MarketSlackNotification from 'src/shared/app/market/component/prompt/MarketSlackNotification';
import MarketFavoriteNotification from 'src/shared/app/market/component/prompt/MarketFavoriteNotification';
import {
  notificationInfoLevel,
  notificationTopType,
} from 'src/shared/app/notification/constant/notificationConstants';
import {
  highlightsFilter,
  justCreatedFilter,
  attendeesFilter,
  companyFilter,
  bookmarkedParam,
} from 'src/shared/app/market/settings/marketSettings';

import type { BraindateEvent } from '@e180/domain/lib/event/type';
import type { Notification } from 'src/shared/app/notification/type/notificationTypes';

export function parseTagCloudsFromFeaturedKeywords(
  categories: Object,
  // $FlowIssue
  intl: intlShape,
  event: BraindateEvent,
): Array<Object> {
  const maxCount = 5;

  let data = [];

  if (categories && typeof categories === 'object') {
    if (highlightsFilter in categories) {
      const keywords = categories[highlightsFilter];

      if (Array.isArray(keywords) && keywords.length) {
        data = [
          ...data,
          {
            label: intl.formatMessage(messages.highlightsFilter),
            value: highlightsFilter,
            tags: keywords.map(parseTagFromFeaturedKeyword).slice(0, maxCount),
          },
        ];
      }
    }

    if (justCreatedFilter in categories) {
      const keywords = categories[justCreatedFilter];

      if (Array.isArray(keywords) && keywords.length) {
        data = [
          ...data,
          {
            label: intl.formatMessage(messages.justCreatedFilter),
            value: justCreatedFilter,
            tags: keywords.map(parseTagFromFeaturedKeyword).slice(0, maxCount),
          },
        ];
      }
    }

    if (attendeesFilter in categories) {
      const keywords = categories[attendeesFilter];

      if (Array.isArray(keywords) && keywords.length) {
        data = [
          ...data,
          {
            label:
              getEventProfilePositionLabel(event, intl.locale) ||
              intl.formatMessage(messages.attendeesFilter),
            value: attendeesFilter,
            tags: keywords.map(parseTagFromFeaturedKeyword).slice(0, maxCount),
          },
        ];
      }
    }

    if (companyFilter in categories) {
      const keywords = categories[companyFilter];

      if (Array.isArray(keywords) && keywords.length) {
        data = [
          ...data,
          {
            label:
              getEventProfileCompanyLabel(event, intl.locale) ||
              intl.formatMessage(messages.companyFilter),
            value: companyFilter,
            tags: keywords.map(parseTagFromFeaturedKeyword).slice(0, maxCount),
          },
        ];
      }
    }
  }

  return data;
}

export function parseTagFromFeaturedKeyword(keyword: string): Object {
  return {
    value: keyword,
    label: keyword,
  };
}

export function convertSlackSharingToNotification(): Notification {
  return {
    id: 'slack-sharing-topic-created',
    level: notificationInfoLevel,
    type: notificationTopType,
    componentClass: MarketSlackNotification,
  };
}

export function convertFavoriteTutorialToNotification(): Notification {
  return {
    id: 'favorite-first-time-notiifcation',
    level: notificationInfoLevel,
    type: notificationTopType,
    componentClass: MarketFavoriteNotification,
  };
}

export function countActiveFilters(filters: Object): number {
  assertObject(filters, 'filters');

  let count = 0;

  for (const key in filters) {
    if (typeof filters[key] === 'string' && filters[key]) {
      // The favorite filter must not be counted
      if (filters[key] !== bookmarkedParam) {
        count += 1;
      }
    }
  }

  return count;
}
